/** @jsxImportSource @emotion/react */
import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { css, Global } from '@emotion/react'
import { App, Select, Spin, Switch } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import useConstraintTypes from '../../hooks/useConstraintTypes'
import type { CreateConstraintData } from '../../hooks/useSchedulerConstraints'
import type { Venue } from '../../hooks/useSchedulerLocations'
import type { ConstraintTier } from '../../constants'
import { mapCategoryToDisplay, getConstraintDisplayName } from '../../constants'
import { darkPortalStyles } from '../../styles/shared'
import {
  buildConstraintParameters,
  getDefaultValues,
  validateConstraintValues,
} from '../../utils/constraintFormUtils'
import ConstraintParameterFields, {
  HardSoftToggle,
} from './ConstraintParameterFields'

const { OptGroup, Option } = Select

interface InlineConstraintFormProps {
  teamId?: string
  divisionId: string
  onSave: (data: CreateConstraintData) => Promise<unknown>
  onCancel: () => void
  venues?: Venue[]
  tier?: ConstraintTier
  /**
   * Constraint codes to exclude from the type dropdown. Used by callers
   * (e.g. the per-team picker) to hide types that have a dedicated
   * editing surface elsewhere — pair_matchup_count lives in Matchup
   * Config, not the per-team picker.
   */
  excludeCodes?: readonly string[]
}

// Picker grouping helper lives in inlineConstraintFormHelpers.ts so it
// can be unit-tested without dragging the JSX module through Jest.
import { groupConstraintTypesForPicker } from './inlineConstraintFormHelpers'

export { groupConstraintTypesForPicker } from './inlineConstraintFormHelpers'

const InlineConstraintForm: React.FC<InlineConstraintFormProps> = ({
  teamId,
  divisionId,
  onSave,
  onCancel,
  venues = [],
  tier = 'team',
  excludeCodes,
}) => {
  const { message } = App.useApp()
  const { constraintTypes } = useConstraintTypes(tier)
  const [selectedTypeCode, setSelectedTypeCode] = useState<string | null>(null)
  const [isHard, setIsHard] = useState(false)
  const [isEnabled, setIsEnabled] = useState(true)
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [saving, setSaving] = useState(false)

  // Group constraint types by category for the dropdown
  const groupedTypes = useMemo(
    () => groupConstraintTypesForPicker(constraintTypes, excludeCodes),
    [constraintTypes, excludeCodes]
  )

  // When a type is selected, set defaults
  useEffect(() => {
    if (selectedTypeCode) {
      const typeDef = constraintTypes.find((t) => t.code === selectedTypeCode)
      if (typeDef) {
        setIsHard(typeDef.is_hard_default)
      }
      setValues(getDefaultValues(selectedTypeCode))
    }
  }, [selectedTypeCode, constraintTypes])

  // Escape key cancels
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onCancel])

  const handleFieldChange = useCallback((field: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleSave = useCallback(async () => {
    if (!selectedTypeCode) {
      return
    }

    const error = validateConstraintValues(selectedTypeCode, values)
    if (error) {
      message.warning(error)
      return
    }

    setSaving(true)
    try {
      const parameters = buildConstraintParameters(selectedTypeCode, values)
      const typeDef = constraintTypes.find((t) => t.code === selectedTypeCode)
      const data: CreateConstraintData = {
        constraint_type_code: selectedTypeCode,
        parameters,
        description: typeDef?.name || undefined,
        is_hard: isHard,
        is_enabled: isEnabled,
        team_uids: teamId ? [teamId] : null,
        division_uids: [divisionId],
      }
      await onSave(data)
      message.success('Constraint added')
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message)
      } else {
        message.error('Failed to add constraint')
      }
    } finally {
      setSaving(false)
    }
  }, [
    selectedTypeCode,
    values,
    isHard,
    isEnabled,
    teamId,
    divisionId,
    onSave,
    constraintTypes,
    message,
  ])

  // Enter to save for simple single-field types
  useEffect(() => {
    if (!selectedTypeCode) {
      return
    }
    const simpleCodes = [
      'min_rest_days',
      'no_back_to_back',
      'max_games_per_team_per_day',
      'games_per_team_per_week',
    ]
    if (!simpleCodes.includes(selectedTypeCode)) {
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !saving) {
        handleSave()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [selectedTypeCode, saving, handleSave])

  return (
    <>
      <Global styles={darkPortalStyles} />
      <div css={cardContainer}>
        {/* Header */}
        <div css={cardHeader}>
          <div css={headerContent}>
            <h2 css={headerTitle}>Add Constraint</h2>
            <button css={closeBtn} onClick={onCancel}>
              <CloseOutlined />
            </button>
          </div>
        </div>

        {/* Body */}
        <div css={cardBody}>
          {/* Row 1: Type + Active */}
          <div css={typeActiveRow}>
            <div css={typeActiveSection}>
              <div css={typeActiveInfo}>
                <span css={typeActiveLabel}>Type</span>
                <span css={typeActiveDesc}>
                  How the constraint affects schedule
                </span>
              </div>
              <HardSoftToggle value={isHard} onChange={setIsHard} />
            </div>
            <div css={verticalDivider} />
            <div css={typeActiveSection}>
              <div css={typeActiveInfo}>
                <span css={typeActiveLabel}>Active</span>
                <span css={typeActiveDesc}>
                  The constraint affects the schedule
                </span>
              </div>
              <Switch
                size='small'
                checked={isEnabled}
                onChange={setIsEnabled}
                css={switchOverride}
              />
            </div>
          </div>

          {/* Row 2: Constraint type dropdown */}
          <div css={fieldContainer}>
            <span css={fieldLabel}>Constraint</span>
            <Select
              placeholder='Select constraint type...'
              value={selectedTypeCode}
              onChange={setSelectedTypeCode}
              popupClassName='dark-select-popup'
              showSearch
              optionFilterProp='children'
              autoFocus
            >
              {Array.from(groupedTypes.entries()).map(([category, types]) => (
                <OptGroup key={category} label={mapCategoryToDisplay(category)}>
                  {types.map((t) => (
                    <Option key={t.code} value={t.code}>
                      {getConstraintDisplayName(t.code, t.name)}
                    </Option>
                  ))}
                </OptGroup>
              ))}
            </Select>
          </div>

          {/* Row 3: Parameter fields (conditional on selectedTypeCode) */}
          {selectedTypeCode && (
            <ConstraintParameterFields
              typeCode={selectedTypeCode}
              values={values}
              onChange={handleFieldChange}
              venues={venues}
              fullWidth
            />
          )}
        </div>

        {/* Footer */}
        <div css={cardFooter}>
          <div css={footerButtons}>
            <button css={cancelBtn} onClick={onCancel}>
              Cancel
            </button>
            <button
              css={confirmBtn}
              onClick={handleSave}
              disabled={saving || !selectedTypeCode}
            >
              {saving ? <Spin size='small' /> : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// ─── Styles ──────────────────────────────────────────

const cardContainer = css`
  background: ${colors.SECONDARY_400};
  border-bottom: 1px solid ${colors.SECONDARY_300};

  .ant-input,
  .ant-input-number,
  .ant-picker {
    background-color: ${colors.SECONDARY_300} !important;
    border-color: ${colors.SECONDARY_200} !important;
    color: ${colors.WHITE} !important;
    border-radius: 6px !important;
    min-height: 32px !important;
    box-shadow: none !important;
  }

  .ant-input:focus,
  .ant-input-focused,
  .ant-input-number:focus,
  .ant-input-number-focused,
  .ant-picker-focused {
    border-color: ${colors.SECONDARY_100} !important;
    box-shadow: none !important;
  }

  .ant-input-number-input {
    color: ${colors.WHITE} !important;
  }

  .ant-input-number-group-addon {
    background-color: ${colors.SECONDARY_300} !important;
    border-color: ${colors.SECONDARY_200} !important;
    color: ${colors.NEUTRAL_50} !important;
  }

  .ant-select-selector {
    background-color: ${colors.SECONDARY_300} !important;
    border-color: ${colors.SECONDARY_200} !important;
    color: ${colors.WHITE} !important;
    border-radius: 6px !important;
    min-height: 32px !important;
    box-shadow: none !important;
  }

  .ant-select-focused .ant-select-selector {
    border-color: ${colors.SECONDARY_100} !important;
    box-shadow: none !important;
  }

  .ant-select-arrow,
  .ant-picker-suffix {
    color: ${colors.NEUTRAL_100};
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    color: ${colors.WHITE};
  }

  .ant-picker-input > input {
    color: ${colors.WHITE} !important;
  }

  .ant-switch {
    background-color: ${colors.NEUTRAL_600};
  }

  .ant-switch-checked {
    background-color: ${colors.PRIMARY} !important;
  }
`

const cardHeader = css`
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.SECONDARY_300};
`

const headerContent = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const headerTitle = css`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.6px;
  line-height: 16px;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
  margin: 0;
`

const closeBtn = css`
  background: transparent;
  border: none;
  color: ${colors.NEUTRAL_70};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;

  &:hover {
    color: ${colors.WHITE};
  }
`

const cardBody = css`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const typeActiveRow = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

const typeActiveSection = css`
  display: flex;
  flex: 1;
  gap: 8px;
  align-items: flex-start;
  min-width: 0;
`

const typeActiveInfo = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const typeActiveLabel = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: ${colors.WHITE};
`

const typeActiveDesc = css`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 15px;
  color: ${colors.NEUTRAL_100};
`

const verticalDivider = css`
  width: 1px;
  align-self: stretch;
  background-color: ${colors.SECONDARY_300};
`

const fieldContainer = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const fieldLabel = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: ${colors.NEUTRAL_50};
`

const switchOverride = css`
  &.ant-switch-checked {
    background-color: ${colors.PRIMARY} !important;
  }
`

const cardFooter = css`
  padding: 12px 16px;
  border-top: 1px solid ${colors.SECONDARY_300};
  display: flex;
  justify-content: flex-end;
`

const footerButtons = css`
  display: flex;
  gap: 8px;
`

const cancelBtn = css`
  padding: 6px 20px;
  background: transparent;
  border: 1px solid ${colors.NEUTRAL_100};
  border-radius: 6px;
  color: ${colors.NEUTRAL_50};
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;

  &:hover {
    border-color: ${colors.WHITE};
    color: ${colors.WHITE};
  }
`

const confirmBtn = css`
  padding: 6px 20px;
  background: ${colors.PRIMARY};
  border: none;
  border-radius: 6px;
  color: ${colors.PRIMARY_TEXT};
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;

  &:hover {
    background: ${colors.PRIMARY_HOVER};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export default InlineConstraintForm
